<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Immigration Proceeding"
    :section="section"
    has-footer
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row class="mt-4">
        <v-col cols="3">
          <v-autocomplete
            v-model="section.hearing_type"
            :items="optionsHearingTypes"
            label="Hearing Type"
            outlined
            :error-messages="getErrorMessages(v$.section.hearing_type)"
            @input="v$.section.hearing_type.$touch()"
            @blur="v$.section.hearing_type.$touch()"
          />
        </v-col>
        <!-- Outcomes -->
        <v-col
          v-if="fieldRules.removal_outcome"
          cols="3"
        >
          <v-autocomplete
            v-model="section.removal_outcome"
            :items="optionsRemovalOutcomes"
            label="Removal Outcome"
            outlined
            :error-messages="getErrorMessages(v$.section.removal_outcome)"
            @input="v$.section.removal_outcome.$touch()"
            @blur="v$.section.removal_outcome.$touch()"
          />
        </v-col>

        <v-col
          v-if="fieldRules.expedited_removal_outcome"
          cols="3"
        >
          <v-autocomplete
            v-model="section.expedited_removal_outcome"
            :items="optionsExpeditedRemovalOutcomes"
            label="Expedited Removal Outcome"
            outlined
            :error-messages="getErrorMessages(v$.section.expedited_removal_outcome)"
            @input="v$.section.expedited_removal_outcome.$touch()"
            @blur="v$.section.expedited_removal_outcome.$touch()"
          />
        </v-col>
        <!-- Complements -->
        <v-col
          v-if="fieldRules.charges"
        >
          <v-autocomplete
            v-model="section.charges"
            :items="optionsCharges"
            label="Charges"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.charges)"
            @input="v$.section.charges.$touch()"
            @blur="v$.section.charges.$touch()"
          />
        </v-col>

        <v-col
          v-if="fieldRules.appeal_jurisdiction"
          cols="3"
        >
          <v-autocomplete
            v-model="section.appeal_jurisdiction"
            :items="optionsAppealJurisdictions"
            label="Admin. Appeal Jurisdiction"
            outlined
            :error-messages="getErrorMessages(v$.section.appeal_jurisdiction)"
            @input="v$.section.appeal_jurisdiction.$touch()"
            @blur="v$.section.appeal_jurisdiction.$touch()"
          />
        </v-col>

        <v-col
          v-if="fieldRules.federal_action"
          cols="3"
        >
          <v-autocomplete
            v-model="section.federal_action"
            :items="optionsFederalActions"
            label="Federal Action"
            outlined
            :error-messages="getErrorMessages(v$.section.federal_action)"
            @input="v$.section.federal_action.$touch()"
            @blur="v$.section.federal_action.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.proceeding_jurisdiction"
            autocomplete="off"
            label="Proceeding Jurisdiction"
            outlined
            :error-messages="getErrorMessages(v$.section.proceeding_jurisdiction)"
            @input="v$.section.proceeding_jurisdiction.$touch()"
            @blur="v$.section.proceeding_jurisdiction.$touch()"
          />
        </v-col>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Initiated Date"
            :formatable-date="section.initiated_date"
            :format="section.initiated_date_format"
            :uncertain="section.initiated_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.initiated_date)"
            @update-box="onInitiatedDateUncertainUpdate"
            @update-date="onInitiatedDateUpdate"
            @tab-out="v$.section.initiated_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Outcome Date"
            :formatable-date="section.outcome_date"
            :format="section.outcome_date_format"
            :uncertain="section.outcome_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.outcome_date)"
            @update-box="onOutcomeDateUncertainUpdate"
            @update-date="onOutcomeDateUpdate"
            @tab-out="v$.section.outcome_date.$touch()"
          />
        </v-col>
        <v-col>
          <BasicDate
            :date="section.hearing_date"
            :small-columns="12"
            :columns="12"
            :filled="false"
            label="Next Hearing Date"
            outlined
            :disabled="locked"
            for-party-edit
            @update-date="onHearingDateUpdate"
            @tab-out="v$.section.hearing_date.$touch()"
          />
        </v-col>
      </v-row>
      <!-- Fear block -->
      <v-row>
        <v-col
          v-if="fieldRules.immigration_judge"
          cols="3"
        >
          <v-text-field
            v-model="section.immigration_judge"
            autocomplete="off"
            label="Immigration Judge"
            outlined
            :error-messages="getErrorMessages(v$.section.immigration_judge)"
            @input="v$.section.immigration_judge.$touch()"
            @blur="v$.section.immigration_judge.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            v-model="section.fear_expressed"
            :items="optionsFearExpressed"
            label="Fear Expresed"
            outlined
            :error-messages="getErrorMessages(v$.section.fear_expressed)"
            @input="v$.section.fear_expressed.$touch()"
            @blur="v$.section.fear_expressed.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="showFearBlock">
        <v-col>
          <v-text-field
            v-model="section.fear_ao_jurisdiction"
            autocomplete="off"
            label="Fear AO Jurisdiction"
            outlined
            :error-messages="getErrorMessages(v$.section.fear_ao_jurisdiction)"
            @input="v$.section.fear_ao_jurisdiction.$touch()"
            @blur="v$.section.fear_ao_jurisdiction.$touch()"
          />
        </v-col>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Fear Date"
            :formatable-date="section.fear_date"
            :format="section.fear_date_format"
            :uncertain="section.fear_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.fear_date)"
            @update-box="onFearDateUncertainUpdate"
            @update-date="onFearDateUpdate"
            @tab-out="v$.section.fear_date.$touch()"
          />
        </v-col>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Fear Outcome Date"
            :formatable-date="section.fear_interview_outcome_date"
            :format="section.fear_interview_outcome_date_format"
            :uncertain="section.fear_interview_outcome_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.fear_interview_outcome_date)"
            @update-box="onFearOutcomeDateUncertainUpdate"
            @update-date="onFearOutcomeDateUpdate"
            @tab-out="v$.section.fear_interview_outcome_date.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.fear_interview_outcome"
            :items="optionsFearInterviewOutcomes"
            label="Fear Interview Outcome"
            outlined
            :error-messages="getErrorMessages(v$.section.fear_interview_outcome)"
            @input="v$.section.fear_interview_outcome.$touch()"
            @blur="v$.section.fear_interview_outcome.$touch()"
          />
        </v-col>
      </v-row>
      <v-row v-if="showFearBlock">
        <v-col cols="3">
          <v-text-field
            v-model="section.fear_review_jurisdiction"
            autocomplete="off"
            label="Fear Review Jurisdiction"
            outlined
            :error-messages="getErrorMessages(v$.section.fear_review_jurisdiction)"
            @input="v$.section.fear_review_jurisdiction.$touch()"
            @blur="v$.section.fear_review_jurisdiction.$touch()"
          />
        </v-col>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Fear Review Date"
            :formatable-date="section.fear_review_date"
            :format="section.fear_review_date_format"
            :uncertain="section.fear_review_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.fear_review_date)"
            @update-box="onFearReviewDateUncertainUpdate"
            @update-date="onFearReviewDateUpdate"
            @tab-out="v$.section.fear_review_date.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            v-model="section.fear_review_outcome"
            :items="optionsFearReviewOutcomes"
            label="Fear Review Outcome"
            outlined
            :error-messages="getErrorMessages(v$.section.fear_review_outcome)"
            @input="v$.section.fear_review_outcome.$touch()"
            @blur="v$.section.fear_review_outcome.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'
import { hearingStatusReqChecker } from '@/store/party/partials/party-edit/helpers/pe-immigration-proceedings-card'

import BasicDate from '@/components/Fields/BasicDate'
import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'ImmigrationProceedingsCard',
  components: { BasicDate, FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      documentTypes: [
        { text: 'Charging Document', value: 'charging-document' },
      ],

      section: {},
    }
  },

  validations () {
    return {
      section: {
        appeal_jurisdiction: {},
        charges: {},
        expedited_removal_outcome: {},
        fear_ao_jurisdiction: {},
        fear_date: {},
        fear_date_uncertain: {},
        fear_expressed: {},
        fear_interview_outcome: {},
        fear_interview_outcome_date: {},
        fear_outcome_date_uncertain: {},
        fear_review_date: {},
        fear_review_date_uncertain: {},
        fear_review_jurisdiction: {},
        fear_review_outcome: {},
        federal_action: {},
        hearing_date: {},
        hearing_type: {},
        immigration_judge: {},
        initiated_date: {},
        initiated_date_uncertain: {},
        notes: {},
        outcome_date: {},
        outcome_date_uncertain: {},
        proceeding_jurisdiction: {},
        removal_outcome: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `immigration-proceedings-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    documentIdentifier () { return `background-immigration-proceedings-${this.sectionIdentifier}` },
    fieldRules () { return hearingStatusReqChecker(this.section.hearing_type) },

    locked: {
      get () {
        return this.peImmigrationProceedingsCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeImmigrationProceedingsCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsAppealJurisdictions () {
      return this.getCachedDropdown('immigration_proceedings/appeal_jurisdiction')
    },

    optionsCharges () {
      return this.getCachedDropdown('immigration_proceedings/charges')
    },

    optionsExpeditedRemovalOutcomes () {
      return this.getCachedDropdown('immigration_proceedings/expedited_removal_outcome')
    },

    optionsFearExpressed () {
      return this.getCachedDropdown('immigration_proceedings/fear_expressed')
    },

    optionsFearInterviewOutcomes () {
      return this.getCachedDropdown('immigration_proceedings/fear_interview_outcome')
    },

    optionsFearReviewOutcomes () {
      return this.getCachedDropdown('immigration_proceedings/fear_review_outcome')
    },

    optionsFederalActions () {
      return this.getCachedDropdown('immigration_proceedings/federal_action')
    },

    optionsHearingTypes () {
      return this.getCachedDropdown('immigration_proceedings/hearing_type')
    },

    optionsRemovalOutcomes () {
      return this.getCachedDropdown('immigration_proceedings/removal_outcome')
    },

    sectionIdentifier () { return this.section.id || 'new' },

    showFearBlock () {
      return hearingStatusReqChecker(this.section.hearing_type).fear_block &&
      this.section.fear_expressed === 'yes'
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peImmigrationProceedingsCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeImmigrationProceedingsCard(this.sectionIdentifier)

    this.getDropdownOptions({ path: 'immigration_proceedings/hearing_type' })
    this.getDropdownOptions({ path: 'immigration_proceedings/removal_outcome' })
    this.getDropdownOptions({ path: 'immigration_proceedings/expedited_removal_outcome' })
    this.getDropdownOptions({ path: 'immigration_proceedings/charges' })
    this.getDropdownOptions({ path: 'immigration_proceedings/appeal_jurisdiction' })
    this.getDropdownOptions({ path: 'immigration_proceedings/federal_action' })
    this.getDropdownOptions({ path: 'immigration_proceedings/fear_expressed' })
    this.getDropdownOptions({ path: 'immigration_proceedings/fear_interview_outcome' })
    this.getDropdownOptions({ path: 'immigration_proceedings/fear_review_outcome' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peImmigrationProceedingsCardCreate({
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onCardDelete () {
      if (this.create) return this.peImmigrationProceedingsCardCreateUndo()
      else {
        return this.peImmigrationProceedingsCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peImmigrationProceedingsCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peImmigrationProceedingsCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onFearDateUncertainUpdate (value) {
      this.v$.section.fear_date.$touch()
      this.v$.section.fear_date_uncertain.$touch()
      this.section.fear_date_uncertain = value
    },

    onFearDateUpdate (value) {
      this.v$.section.fear_date.$touch()
      this.v$.section.fear_date_uncertain.$touch()
      this.section.fear_date = value.date
      this.section.fear_date_format = value.format
    },

    onFearOutcomeDateUncertainUpdate (value) {
      this.v$.section.fear_interview_outcome_date.$touch()
      this.v$.section.fear_outcome_date_uncertain.$touch()
      this.section.fear_outcome_date_uncertain = value
    },

    onFearOutcomeDateUpdate (value) {
      this.v$.section.fear_interview_outcome_date.$touch()
      this.v$.section.fear_outcome_date_uncertain.$touch()
      this.section.fear_interview_outcome_date = value.date
      this.section.fear_interview_outcome_date_format = value.format
    },

    onFearReviewDateUncertainUpdate (value) {
      this.v$.section.fear_review_date.$touch()
      this.v$.section.fear_review_date_uncertain.$touch()
      this.section.fear_review_date_uncertain = value
    },

    onFearReviewDateUpdate (value) {
      this.v$.section.fear_review_date.$touch()
      this.v$.section.fear_review_date_uncertain.$touch()
      this.section.fear_review_date = value.date
      this.section.fear_review_date_format = value.format
    },

    onHearingDateUpdate (value) {
      this.v$.section.hearing_date.$touch()
      this.section.hearing_date = value.date
      this.section.hearing_date_format = value.format
    },

    onInitiatedDateUncertainUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date_uncertain = value
    },

    onInitiatedDateUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date = value.date
      this.section.initiated_date_format = value.format
    },

    // on-outcome_date update
    onOutcomeDateUncertainUpdate (value) {
      this.v$.section.outcome_date.$touch()
      this.v$.section.outcome_date_uncertain.$touch()
      this.section.outcome_date_uncertain = value
    },

    onOutcomeDateUpdate (value) {
      this.v$.section.outcome_date.$touch()
      this.v$.section.outcome_date_uncertain.$touch()
      this.section.outcome_date = value.date
      this.section.outcome_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeImmigrationProceedingsCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peImmigrationProceedingsCardCreateUndo',
      'setPeImmigrationProceedingsCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeImmigrationProceedingsCard',
      'peImmigrationProceedingsCardCreate',
      'peImmigrationProceedingsCardDelete',
      'peImmigrationProceedingsCardRefresh',
      'peImmigrationProceedingsCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
